import { useEffect, useMemo, useState } from 'react';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import NoData from '@bumble/app/components/NoData';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '@bumble/app/components/Loading';
import { Indicator } from '@bumble/app/components/Indicator';
import { AppServicesHostingAdd } from './AppServicesHostingAdd';

const columns: GridColDef[] = [
  {
    field: 'hosting_id',
    headerName: 'Hosting Id',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 100,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  {
    field: 'priority_id',
    headerName: 'Priority Id',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'hosting_type',
    headerName: 'Hosting Type',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'region',
    headerName: 'Region',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'service_provider',
    headerName: 'Service Provider',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'cloud_endpoint_service_name',
    headerName: 'Cloud Endpoint Service Name',
    width: 400,
    disableColumnMenu: true,
  },
  {
    field: 'fqdn',
    headerName: 'App DNS Name',
    width: 400,
    disableColumnMenu: true,
  },
  {
    field: 'size',
    headerName: 'Size',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'health',
    headerName: 'Health',
    width: 100,
    disableColumnMenu: true,
  },
];

const columnVisible = {
  admin_state: true,
  op_state: true,
  hosting_id: true,
  hosting_type: true,
  region: true,
  service_provider: true,
  cloud_endpoint_service_name: true,
  fqdn: true,
  priority_id: true,
  size: true,
  health: true,
};

export const AppServicesTabHosting = ({ id }: { id: string }) => {
  const api = useApi();
  const [showHostingRemove, setShowHostingRemove] = useState<boolean>(false);
  const [showHostingAdd, setShowHostingAdd] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const { isLoading, data, refetch } = useQuery(
    ['appServiceHosting'],
    api.appServiceGetHostingApiV1AppServiceGetHostingGet.bind(null, {
      app_service_id: id,
    }),
    { enabled: true, cacheTime: 0 },
  );

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed data.');
  };

  const handleAdd = () => {
    setShowHostingAdd(true);
  };

  const removeHosting = useMutation(api.appServiceRemoveHostingApiV1AppServiceRemoveHostingPost, {
    onSuccess() {
      toast.success('Hosting removed.');
      refetch();
    },
    onError: apiError,
  });

  const handleRemove = () => {
    const hostId =
      data?.data?.filter((item) => item.hosting_id === selectedItemId)[0].hosting_id || '';
    removeHosting.mutate({
      app_service_id: id,
      hosting_id: hostId,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (id === undefined || !data || data?.data.length === 0) {
    return <NoData />;
  }

  return (
    <>
      <DataTableGrid
        rows={
          data?.data.map((item) => {
            return {
              ...item,
              id: item.hosting_id,
            };
          }) || []
        }
        columns={columns}
        handleSelectionChange={handleSelectionChange}
        rowSelectionModel={rowSelectionModel}
        itemsSelected={rowSelectionModel.length === 1}
        columnVisibilityModel={columnVisible}
        newLabel="Add Hosting"
        newOnClick={handleAdd}
        refreshData={handleDataRefetch}
        menuItems={[
          {
            label: 'Remove Hosting',
            icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
            onClick: () => setShowHostingRemove(true),
          },
        ]}
      />
      {showHostingAdd && (
        <CreateModal
          open={showHostingAdd}
          handleClose={() => setShowHostingAdd(false)}
          title="Add Hosting"
        >
          <AppServicesHostingAdd appServiceId={id} handleClose={() => setShowHostingAdd(false)} />
        </CreateModal>
      )}
      {showHostingRemove && (
        <DialogConfirm
          open={showHostingRemove}
          handleClose={() => setShowHostingRemove(false)}
          handleConfirm={handleRemove}
          title="Remove Hosting?"
          description={`Are you certain you wish to proceed? Please note that this action is irreversible and will result in the permanent removal of hosting ${selectedItemId}.`}
          confirmationPhrase={selectedItemId}
        />
      )}
    </>
  );
};
